<!-- schedule-x 4.9 keeps every visited month's cells mounted, which slows month changes; patches/@schedule-x__calendar@4.9.1.patch unmounts them. -->
<script setup lang="ts">
import type { Temporal } from 'temporal-polyfill';
import { computed } from 'vue';
import { plainDateFromLocalDate, useUserLocale } from '#layers/base/public';
import { CALENDAR_DAY_LABEL_FORMAT } from '#layers/listing/constants/calendar';

const {
	jsDate,
	today,
} = defineProps<{
	jsDate: Date;
	today: Temporal.PlainDate;
}>();

defineEmits<{
	open: [date: Temporal.PlainDate];
}>();

const { formatLocale } = useUserLocale();

const date = computed(() => plainDateFromLocalDate(jsDate));

const isToday = computed(() => date.value.equals(today));

const monthLabel = computed(() => date.value.day === 1 ? date.value.toLocaleString(formatLocale.value, { month: 'short' }) : null);

const label = computed(() => date.value.toLocaleString(formatLocale.value, CALENDAR_DAY_LABEL_FORMAT));
</script>

<template>
	<button
		type="button"
		data-testid="calendar-month-grid-date"
		:aria-label="label"
		:aria-current="isToday ? 'date' : undefined"
		class="
			mb-1 flex h-5.5 min-w-5.5 items-center justify-center gap-1 rounded-sm px-1 text-xs font-semibold tabular-nums
			focus:outline-none
			focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset
		"
		:class="isToday ? 'bg-primary text-inverted' : `
			text-highlighted
			hover:bg-elevated
			in-[.is-leading-or-trailing]:font-normal in-[.is-leading-or-trailing]:text-dimmed
		`"
		@click.stop="$emit('open', date)"
	>
		<span
			v-if="monthLabel"
			class="font-normal"
			:class="{ 'text-muted': !isToday }"
		>{{ monthLabel }}</span>
		{{ date.day }}
	</button>
</template>
